import { useState, FormEvent } from "react";
import { submitRegistration } from "../../lib/api";
import { Link } from "react-router-dom";

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  home_number: string;
  company: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
}

const initialForm: FormState = {
  first_name: "",
  last_name: "",
  email: "",
  mobile_number: "",
  home_number: "",
  company: "",
  street: "",
  city: "Brooklyn",
  state: "NY",
  zip: "",
  notes: "",
};

type SubmitStatus = "idle" | "submitting" | "success" | "error";

export default function RegisterPage() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.mobile_number) return;
    if (form.notes.length > 500) return;

    setStatus("submitting");
    setErrorMsg("");

    const result = await submitRegistration(form);
    if (result.success) {
      setStatus("success");
    } else {
      setStatus("error");
      setErrorMsg(result.message || "Something went wrong. Please try again or call us at (929) 678-1640.");
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const inputClass = "w-full rounded-xl px-4 py-3 text-sm text-gray-800 outline-none transition-all focus:ring-2";
  const inputStyle: React.CSSProperties = {
    background: "#f7fbff",
    border: "1px solid rgba(74,159,212,0.25)",
  };
  const inputFocusStyle = "focus:ring-[#4a9fd4]/30";

  return (
    <div className="min-h-screen w-full" style={{ background: "linear-gradient(135deg,#dce9f5 0%,#e8f2fa 40%,#f0f7fd 100%)" }}>
      {/* Top nav bar */}
      <div className="w-full" style={{ background: "#1a5f8a" }}>
        <div className="max-w-7xl mx-auto px-8 py-3 flex items-center justify-between">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://thermalxpert.nyc/wp-content/uploads/2024/03/ThermalExpert-Color-TransperentBG-300x51-1.png"
              alt="ThermalXpert"
              className="h-8 w-auto object-contain brightness-0 invert"
            />
          </Link>
          <div className="flex items-center gap-6 text-xs text-white/80">
            <div className="hidden sm:flex items-center gap-1.5">
              <i className="ri-phone-fill text-white/60"></i>
              <span>(929) 678-1640</span>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-14">
        {/* Page header */}
        <div className="text-center mb-12">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
            style={{ background: "rgba(74,159,212,0.12)", color: "#2d7ab5", border: "1px solid rgba(74,159,212,0.25)" }}
          >
            <i className="ri-user-add-line"></i>
            Customer Portal
          </div>
          <h1 className="text-4xl md:text-5xl font-black leading-tight" style={{ color: "#1a3a52" }}>
            Register as a <span style={{ color: "#4a9fd4" }}>Customer</span>
          </h1>
          <p className="text-gray-500 mt-3 max-w-lg mx-auto text-sm leading-relaxed">
            Create your account to connect directly with our scheduling system. Once registered, you can book appointments, track service history, and more.
          </p>
        </div>

        {status === "success" ? (
          <div className="max-w-lg mx-auto">
            <div
              className="rounded-3xl p-12 text-center"
              style={{ background: "#fff", border: "1px solid rgba(74,159,212,0.2)" }}
            >
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-6"
                style={{ background: "rgba(74,159,212,0.1)" }}
              >
                <i className="ri-checkbox-circle-fill text-4xl" style={{ color: "#4a9fd4" }}></i>
              </div>
              <h2 className="text-2xl font-black mb-3" style={{ color: "#1a3a52" }}>You&apos;re Registered!</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Welcome to ThermalXpert! Your information has been received and added to our system. Our team will reach out shortly to confirm your account and help you schedule your first service.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://book.housecallpro.com/book/THERMALXPERT/82bac7c9463e4df29b9d2e77332e7c39?v2=true"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="px-6 py-3 rounded-full text-sm font-black text-white cursor-pointer transition-all hover:scale-105 whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}
                >
                  Schedule a Service
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full text-sm font-black cursor-pointer transition-all hover:scale-105 whitespace-nowrap text-center"
                  style={{ background: "rgba(74,159,212,0.1)", color: "#2d7ab5", border: "1px solid rgba(74,159,212,0.25)" }}
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left — info panel */}
            <div className="flex flex-col gap-5">
              {/* Why register card */}
              <div
                className="rounded-3xl p-6 flex flex-col gap-4"
                style={{ background: "#fff", border: "1px solid rgba(74,159,212,0.15)" }}
              >
                <h3 className="font-black text-sm uppercase tracking-widest" style={{ color: "#2d7ab5" }}>Why Register?</h3>
                {[
                  { icon: "ri-calendar-check-fill", title: "Easy Scheduling", desc: "Book appointments online anytime" },
                  { icon: "ri-history-fill", title: "Service History", desc: "Track all your past and upcoming jobs" },
                  { icon: "ri-notification-3-fill", title: "Job Updates", desc: "Get real-time notifications on your service" },
                  { icon: "ri-file-list-3-fill", title: "Digital Invoices", desc: "Access and pay invoices online" },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: "rgba(74,159,212,0.1)" }}
                    >
                      <i className={`${item.icon} text-sm`} style={{ color: "#4a9fd4" }}></i>
                    </div>
                    <div>
                      <p className="text-sm font-black text-gray-800">{item.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact card */}
              <div
                className="rounded-3xl p-6"
                style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}
              >
                <p className="text-xs font-black uppercase tracking-widest text-white/70 mb-3">Need Help?</p>
                <p className="text-white font-black text-base mb-1">Call or Text Us</p>
                <a href="tel:9296781640" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
                  (929) 678-1640
                </a>
                <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                  <p className="text-white font-black text-base mb-1">Email Us</p>
                  <a href="mailto:support@thermalxpert.nyc" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
                    support@thermalxpert.nyc
                  </a>
                </div>
              </div>

              {/* Schedule directly */}
              <div
                className="rounded-3xl p-6 flex flex-col gap-3"
                style={{ background: "#fff", border: "1px solid rgba(74,159,212,0.15)" }}
              >
                <p className="text-xs font-black uppercase tracking-widest" style={{ color: "#2d7ab5" }}>Already a Customer?</p>
                <p className="text-sm text-gray-500 leading-relaxed">Skip registration and book directly through our scheduling portal.</p>
                <a
                  href="https://book.housecallpro.com/book/THERMALXPERT/82bac7c9463e4df29b9d2e77332e7c39?v2=true"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-2 text-sm font-black cursor-pointer transition-all hover:gap-3 whitespace-nowrap"
                  style={{ color: "#4a9fd4" }}
                >
                  Schedule Online <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Right — form */}
            <div className="lg:col-span-2">
              <form
                id="customer-registration-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-3xl p-8 flex flex-col gap-6"
                style={{ background: "#fff", border: "1px solid rgba(74,159,212,0.15)" }}
              >
                <div>
                  <h2 className="text-xl font-black mb-1" style={{ color: "#1a3a52" }}>Personal Information</h2>
                  <p className="text-xs text-gray-400">Fields marked with * are required</p>
                </div>

                {/* Name row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-600">First Name *</label>
                    <input
                      name="first_name"
                      type="text"
                      required
                      placeholder="John"
                      value={form.first_name}
                      onChange={handleChange}
                      className={`${inputClass} ${inputFocusStyle}`}
                      style={inputStyle}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-600">Last Name *</label>
                    <input
                      name="last_name"
                      type="text"
                      required
                      placeholder="Doe"
                      value={form.last_name}
                      onChange={handleChange}
                      className={`${inputClass} ${inputFocusStyle}`}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Contact row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-600">Email Address *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={handleChange}
                      className={`${inputClass} ${inputFocusStyle}`}
                      style={inputStyle}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-600">Mobile Number *</label>
                    <input
                      name="mobile_number"
                      type="tel"
                      required
                      placeholder="(555) 000-0000"
                      value={form.mobile_number}
                      onChange={handleChange}
                      className={`${inputClass} ${inputFocusStyle}`}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Home phone + company */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-600">Home Phone</label>
                    <input
                      name="home_number"
                      type="tel"
                      placeholder="(555) 000-0000"
                      value={form.home_number}
                      onChange={handleChange}
                      className={`${inputClass} ${inputFocusStyle}`}
                      style={inputStyle}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-600">Company / Business Name</label>
                    <input
                      name="company"
                      type="text"
                      placeholder="Optional"
                      value={form.company}
                      onChange={handleChange}
                      className={`${inputClass} ${inputFocusStyle}`}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Divider */}
                <div className="pt-2" style={{ borderTop: "1px solid rgba(74,159,212,0.12)" }}>
                  <h3 className="text-base font-black mb-4" style={{ color: "#1a3a52" }}>Service Address</h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-gray-600">Street Address</label>
                      <input
                        name="street"
                        type="text"
                        placeholder="123 Main Street, Apt 4B"
                        value={form.street}
                        onChange={handleChange}
                        className={`${inputClass} ${inputFocusStyle}`}
                        style={inputStyle}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex flex-col gap-1.5 col-span-1">
                        <label className="text-xs font-bold text-gray-600">City</label>
                        <input
                          name="city"
                          type="text"
                          placeholder="Brooklyn"
                          value={form.city}
                          onChange={handleChange}
                          className={`${inputClass} ${inputFocusStyle}`}
                          style={inputStyle}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-gray-600">State</label>
                        <select
                          name="state"
                          value={form.state}
                          onChange={handleChange}
                          className={`${inputClass} ${inputFocusStyle} cursor-pointer`}
                          style={inputStyle}
                        >
                          {["NY", "NJ", "CT", "PA"].map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-gray-600">ZIP Code</label>
                        <input
                          name="zip"
                          type="text"
                          placeholder="11232"
                          value={form.zip}
                          onChange={handleChange}
                          className={`${inputClass} ${inputFocusStyle}`}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-600">Additional Notes</label>
                  <textarea
                    name="notes"
                    placeholder="Tell us about your HVAC system, any issues, or special requests..."
                    rows={3}
                    maxLength={500}
                    value={form.notes}
                    onChange={handleChange}
                    className={`${inputClass} ${inputFocusStyle} resize-none`}
                    style={inputStyle}
                  />
                  <p className="text-xs text-gray-400 text-right">{form.notes.length}/500</p>
                </div>

                {/* Error */}
                {status === "error" && (
                  <div
                    className="rounded-xl px-4 py-3 text-sm flex items-center gap-2"
                    style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "#dc2626" }}
                  >
                    <i className="ri-error-warning-fill flex-shrink-0"></i>
                    {errorMsg}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full py-4 rounded-2xl text-white font-black text-base cursor-pointer transition-all hover:scale-[1.02] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Registering...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-fill"></i>
                      Create My Account
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  By registering, your information will be added to our Housecall Pro scheduling system. We&apos;ll never share your data with third parties.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer strip */}
      <div className="w-full py-6 mt-8" style={{ borderTop: "1px solid rgba(74,159,212,0.15)", background: "rgba(255,255,255,0.5)" }}>
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-400">© 2024 ThermalXpert. All rights reserved.</p>
          <Link to="/" className="text-xs cursor-pointer transition-colors hover:text-[#4a9fd4] text-gray-400">
            Back to thermalxpert.nyc
          </Link>
        </div>
      </div>
    </div>
  );
}
